import { createApp } from "vue";
import ServiceCallback from "./service/callback";
import ServiceLogin from "./service/login";
import ServicePay from "./service/pay";
import type { NebulaWEBGameSDKAccount } from "../account";
import type { NebulaWEBGameSDKCore } from "../core";
import type { NebulaWEBGameSDKPayment } from "../payment";
import type { SDKLoginResult, SDKPayArgs, SDKPayResult } from "@nebula-sdk/core";

declare global {
  interface Window {
    __NEBULA_WEB_SDK__: { zIndex: number };
  }
}

// 视图服务
export class NebulaWEBGameSDKView {
  // 构造函数
  public constructor(options: {
    core: NebulaWEBGameSDKCore;
    account: NebulaWEBGameSDKAccount;
    payment: NebulaWEBGameSDKPayment;
  }) {
    this.service = options;
    if (!window.__NEBULA_WEB_SDK__) {
      window.__NEBULA_WEB_SDK__ = { zIndex: 100000000 };
    }

    this.service = options;
  }

  // 服务实例
  private readonly service: {
    core: NebulaWEBGameSDKCore;
    account: NebulaWEBGameSDKAccount;
    payment: NebulaWEBGameSDKPayment;
  };

  // 回调函数
  public async callback(args: { orderNo: string; orderToken: string }): Promise<void> {
    await new Promise((resolve) => {
      const app = createApp(ServiceCallback, {
        service: this.service.payment,
        data: {
          orderNo: args.orderNo,
          orderToken: args.orderToken,
        },
        success: () => {
          void this.service.core.scopeRequestTrack({
            eventId: "callback",
            eventName: "回调成功",
            eventGroup: "view",
            eventValue: { args, result: null },
            eventLevel: "info",
          });

          resolve(true);
          app.unmount();
        },
        error: () => {
          void this.service.core.scopeRequestTrack({
            eventId: "callback",
            eventName: "回调失败",
            eventGroup: "view",
            eventValue: { args, result: null },
            eventLevel: "error",
          });

          resolve(false);
          app.unmount();
        },
      });
      app.mount(document.createElement("div"));
    });
  }

  // 用户登录
  public async login(): Promise<SDKLoginResult> {
    return await new Promise((resolve) => {
      const app = createApp(ServiceLogin, {
        service: this.service.account,
        success: (result: SDKLoginResult) => {
          if (result.status === "success") {
            void this.service.core.scopeRequestTrack({
              eventId: "login",
              eventName: "用户登录成功",
              eventGroup: "view",
              eventValue: { args: null, result },
              eventLevel: "info",
            });
            resolve(result);
          } else {
            void this.service.core.scopeRequestTrack({
              eventId: "login",
              eventName: "用户登录失败",
              eventGroup: "view",
              eventValue: { args: null, result },
              eventLevel: "error",
            });
            resolve(result);
            app.unmount();
          }
        },
        error: (result: SDKLoginResult) => {
          void this.service.core.scopeRequestTrack({
            eventId: "login",
            eventName: "取消登录",
            eventGroup: "view",
            eventValue: { args: null, result },
            eventLevel: "info",
          });
          resolve(result);
          app.unmount();
        },
      });
      app.mount(document.createElement("div"));
    });
  }

  // 发起支付
  public async pay(args: SDKPayArgs): Promise<SDKPayResult> {
    return await new Promise((resolve) => {
      const app = createApp(ServicePay, {
        service: this.service.payment,
        data: args,
        success: (result: SDKPayResult) => {
          if (result.status === "success") {
            void this.service.core.scopeRequestTrack({
              eventId: "pay",
              eventName: "用户支付成功",
              eventGroup: "view",
              eventValue: { args: null, result },
              eventLevel: "info",
            });
            resolve(result);
          } else {
            void this.service.core.scopeRequestTrack({
              eventId: "pay",
              eventName: "用户支付失败",
              eventGroup: "view",
              eventValue: { args: null, result },
              eventLevel: "error",
            });
            resolve(result);
          }
          app.unmount();
        },
        error: (result: SDKPayResult) => {
          void this.service.core.scopeRequestTrack({
            eventId: "pay",
            eventName: "取消支付",
            eventGroup: "view",
            eventValue: { args: null, result },
            eventLevel: "info",
          });
          resolve(result);
          app.unmount();
        },
      });
      app.mount(document.createElement("div"));
    });
  }
}
